// ==========================
// Version 2 - components/Seo.jsx (no dependencies)
// ==========================
import React, { useEffect } from 'react';

/**
 * Lightweight SEO helper with no external dependencies.
 * - Sets <title>, meta description, robots.
 * - Writes Open Graph + Twitter tags.
 * - Adds/updates a canonical <link>.
 *
 * We tag everything with data-seo so repeated renders update cleanly.
 */
export default function Seo({
  title = 'Blessed with a Kiss — Your story. Your proof. Forever.',
  description = 'Immortalise the moments that made you who you are. Turn a meaningful memory into verifiable digital proof that is yours to keep.',
  image = '/logo192.png',
  url,
  noindex = false,
  type = 'website',
}) {
  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const path   = typeof window !== 'undefined' ? window.location.pathname : '/';
    const canonical = url || (origin ? origin + path : undefined);
    const absoluteImage = image?.startsWith('http') ? image : (origin ? `${origin}${image}` : image);

    // helpers
    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute('data-seo', '1');
      return el;
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      el.setAttribute('data-seo', '1');
      return el;
    };

    // Title
    if (title) document.title = title;

    // Description
    if (description) setMeta('meta[name="description"]', { name: 'description', content: description });

    // Robots
    setMeta('meta[name="robots"][data-seo]', {
      name: 'robots',
      content: noindex ? 'noindex,nofollow' : 'index,follow',
    });

    // Canonical
    if (canonical) setLink('canonical', canonical);

    // Open Graph
    setMeta('meta[property="og:site_name"][data-seo]', { property: 'og:site_name', content: 'Blessed With A Kiss' });
    if (title)       setMeta('meta[property="og:title"][data-seo]',       { property: 'og:title', content: title });
    if (description) setMeta('meta[property="og:description"][data-seo]', { property: 'og:description', content: description });
    if (canonical)   setMeta('meta[property="og:url"][data-seo]',         { property: 'og:url', content: canonical });
    setMeta('meta[property="og:type"][data-seo]',                          { property: 'og:type', content: type });
    if (absoluteImage) setMeta('meta[property="og:image"][data-seo]',     { property: 'og:image', content: absoluteImage });

    // Twitter
    setMeta('meta[name="twitter:card"][data-seo]', { name: 'twitter:card', content: 'summary_large_image' });
    if (title)       setMeta('meta[name="twitter:title"][data-seo]',       { name: 'twitter:title', content: title });
    if (description) setMeta('meta[name="twitter:description"][data-seo]', { name: 'twitter:description', content: description });
    if (absoluteImage) setMeta('meta[name="twitter:image"][data-seo]',     { name: 'twitter:image', content: absoluteImage });

    // no cleanup (we keep tags around); future renders overwrite via data-seo
  }, [title, description, image, url, noindex, type]);

  return null;
}
// ==========================
// Version 2 - components/Seo.jsx
// ==========================
